<template>
  <input
    v-bind="commonAttrs"
    class="form-control text-right"
    :value="formattedValue"
    @change="updateValueAndError"
    ref="theInput"
    @keypress="handleKeypress"
    @focus="isOnFocus = true"
    @blur="isOnFocus = false"
  />
</template>
<script lang="ts">
import { PropType } from 'vue'
import { ColumnDef } from '../../../common/$models/ModelDef'
import { hasDecimalTypes } from './inputTypes'

/**
 * 文字列から数値のみを抽出 (全角数字を半角に変換)
 * @param value 入力文字列
 * @param allowDecimal 小数点を許可するかどうか
 */
const formatDisplayValueToNumber = (
  value: string,
  allowDecimal: boolean,
): number | '' => {
  if (value === '' || value === null || value === undefined) {
    return ''
  }

  const isMinus = value.startsWith('-')

  // 全角数字を半角数字に変換
  const convertedValue = value.replace(/[０-９．]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
  })

  // 数値のみを抽出するための正規表現
  // 小数点を許可する場合は、数字と小数点を抽出
  const regex = allowDecimal ? /[0-9.]+/g : /[0-9]+/g

  // 数値のみを抽出
  const matches = convertedValue.match(regex)
  if (!matches || matches.length === 0) {
    return ''
  }

  // 抽出した数値を結合
  const extractedNumber = matches.join('')

  // 数値に変換
  const _v = Number(extractedNumber) * (isMinus ? -1 : 1)

  if (isNaN(_v)) {
    return ''
  }

  return allowDecimal ? _v : Math.floor(_v)
}

/**
 * 数値入力用のコンポーネント
 * - カンマ区切り有無を設定可能
 */
export default {
  name: 'NumberInput',
  props: {
    commonAttrs: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    modelValue: {},
    isFormatWithComma: {
      type: Boolean,
      default: false,
    },
    col: {
      type: Object as PropType<ColumnDef>,
      required: true,
    },
  },
  data() {
    return {
      isOnFocus: false,
    }
  },
  computed: {
    formattedValue() {
      if (
        isNaN(this.modelValue) ||
        this.modelValue === null ||
        this.modelValue === undefined
      ) {
        return ''
      }
      // 入力中は カンマ区切りを解除する
      if (this.isFormatWithComma && !this.isOnFocus) {
        return this.modelValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
      return this.modelValue
    },
    hasDecimal() {
      return hasDecimalTypes.includes(this.col.type)
    },
  },
  methods: {
    updateValueAndError(event) {
      // カンマ区切りを解除, 全角数字 to 半角数字
      const convertedValue = formatDisplayValueToNumber(
        event.target.value,
        this.hasDecimal,
      )
      this.$emit('update:model-value', convertedValue)
      if (convertedValue === '') {
        // 不正な値の場合は、空文字列にする
        this.$refs.theInput.value = ''
      }
      // 小数点許可されていないのに 追加されていたら 戻す
      if (!this.hasDecimal && event.target.value.includes('.')) {
        setTimeout(() => {
          this.$refs.theInput.value = this.formattedValue
        }, 10)
      }
    },
    handleKeypress(event) {
      // 数字 および マイナス 以外の入力を無効化する
      const regEx = this.hasDecimal ? /[-0-9,.]/ : /[-0-9,]/
      if (!regEx.test(event.key)) {
        event.preventDefault()
      }
      // Enterキーを押したら、emit 実行
      if (event.key === 'Enter') {
        this.updateValueAndError(event)
      }
    },
  },
}
</script>
